import {Point} from '@/types/primitives';
import {Constants as constants} from '../config/constants';
import {FileMapping, MealFile} from '@/types/data';

export class Helpers {
    public static clientToSvgPoint(targetSvg: SVGSVGElement, groupArea: SVGGElement, point: Point) {
        const initialPoint = targetSvg.createSVGPoint();
        initialPoint.x = point.x;
        initialPoint.y = point.y;

        const resultPoint = initialPoint.matrixTransform(groupArea.getScreenCTM()!.inverse());

        return {
            x: resultPoint.x,
            y: resultPoint.y
        } as Point;
    }

    public static polarToCartesian(center: Point, radius: number, angleInDegrees: number) {
        const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;

        return {
            x: center.x + (radius * Math.cos(angleInRadians)),
            y: center.y + (radius * Math.sin(angleInRadians))
        };
    }

    public static describeArc(initPoint: Point, radius: number, startAngle: number, endAngle: number) {
        const start = this.polarToCartesian(initPoint, radius, endAngle);
        const end = this.polarToCartesian(initPoint, radius, startAngle);

        const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
        return {
            start,
            path: [
                start.x, start.y,
                'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
            ].join(' '),
            end
        };
    }

    public static calculateArcAngle(radius: number, length: number) {
        return (length * 360) / (2 * Math.PI * radius);
    }

    public static calculatePointOnArc(center: Point, angle: number, radius: number) {
        const angleInRadians = (angle - 90) * Math.PI / 180.0;

        return {
            x: center.x + Math.cos(angleInRadians) * radius,
            y: center.y + Math.sin(angleInRadians) * radius
        };
    }

    public static calculateTriangle(top: Point, basement: Point, basementSize: number) {
        const angle = Math.atan2(basement.y - top.y, basement.x - top.x);

        return [
            top,
            {
                x: Math.sin(angle) * basementSize + basement.x,
                y: -Math.cos(angle) * basementSize + basement.y
            },
            {
                x: -Math.sin(angle) * basementSize + basement.x,
                y: Math.cos(angle) * basementSize + basement.y
            }
        ];
    }

    public static normalizeTriangleBasement(height: number, basement: number, multiplier: number) {
        if (height < basement * multiplier) {
            const calculatedMultiplier = height / (basement * multiplier);
            return calculatedMultiplier >= constants.maxDecreaseMultiplier ?
                constants.maxDecreaseMultiplier * basement :
                calculatedMultiplier * basement;
        }

        return basement;
    }

    public static calculateVectorLength(start: Point, end: Point) {
        return Math.sqrt(Math.pow((end.y - start.y), 2) + Math.pow((end.x - start.x), 2));
    }

    public static rotateVector(angle: number, end: Point) {
        angle = angle * (Math.PI / 180);

        const cos = Math.cos(angle);
        const sin = Math.sin(angle);

        return {
            x: Math.round(10000 * (end.x * cos - end.y * sin)) / 10000,
            y: Math.round(10000 * (end.x * sin + end.y * cos)) / 10000
        };
    }

    public static parseJSONFile(file: File) {
        return new Promise((resolve) => {
            const reader: any = new FileReader();

            reader.onload = () => {
                resolve(JSON.parse(reader.result));
            };

            reader.readAsText(file);
        });
    }

    public static verifyJSONData(file: MealFile) {
        if (file.percentages) {
            Object.keys(constants.publicNamesOfCategories).map((category) => {
                if (typeof file.percentages[category] === 'undefined') {
                    return false;
                }
            });
        } else {
            return false;
        }

        return true;
    }

    public static checkPropIsPrepStep(prop: string) {
        const regex = /^prep\d$/;
        return regex.test(prop);
    }

    public static doFullCopy(data: any) {
        return JSON.parse(JSON.stringify(data));
    }
}
